import {BrowserModule} from "@angular/platform-browser";
import {NgModule} from "@angular/core";
import { ErrorHandler } from "@angular/core";
import { BugsnagErrorHandler } from "@bugsnag/plugin-angular";
import {AppRoutingModule} from "./app-routing.module";
import {AppComponent} from "./app.component";
import {HelpComponent} from "./help/help.component";
import {CreditsComponent} from "./credits/credits.component";
import {Counto} from "./counto/counto.directive";
import {DebugLogModalComponent} from "./debug/debug-log.modal.component";
import {MessageModalComponent} from "./message/message.modal.component";
import {MicrophoneCheckModalComponent} from "./microphone/microphone.check.modal.component";
import {NavComponent} from "./nav/nav.component";
import {PleaseWaitComponent} from "./notification/please.wait.component";
import {SelectionModalComponent} from "./reports/reports.selection.modal.component";
import {FluencyModalComponent} from "./reports/reports.fluency.modal.component";
import {SysinfoComponent} from "./sysinfo/sysinfo.component";
import {ReadingModule} from "./reading/reading.module";
import {LibraryModule} from "./library/library.module";
import {ClientConfiguration} from "./services/configuration/client.config";
import {Login} from "./login/login";
import {CommonModule, HashLocationStrategy, LocationStrategy} from "@angular/common";
import {AssignmentService} from "./services/assignment/assignment.service";
import {AudioFormatService} from "./services/audio/audioformat.service";
import {AudioKeyParser} from "./services/audio-playback/audiokey.parser";
import {AudioPlaybackService} from "./services/audio-playback/audioplayback.service";
import {AudioQueueService} from "./services/audio-playback/audioqueue.service";
import {BrowserAgent} from "./services/browser/browser.agent";
import {ContentServerService} from "./services/configuration/content.server.service";
import {DebugService} from "./services/debug/debug.service";
import {EventBus} from "./event/EventBus";
import {FluencyReportAudio} from "./reports/fluency-report-audio";
import {LocalStoragePersistenceStore} from "./services/persistence/local.storage.persistence.store";
import {BugsnagLoggingService} from "./services/logging/bugsnag-logging.service";
import {MessageService} from "./services/notification/message.service";
import {MicrophoneCheckDataService} from "./services/recognition/microphone-check-data.service";
import {MicrophoneService} from "./services/audio/microphone.service";
import {ModalConfig} from "./services/notification/modal.config";
import {NavService} from "./services/nav/nav.service";
import {NetworkService} from "./services/interaction/network.service";
import {PersistenceService} from "./services/persistence/persistence.service";
import {PipelineService} from "./services/pipeline/pipeline.service";
import {PointService} from "./services/points/points.service";
import {RecognizerService} from "./services/recognition/recognizer.service";
import {ReportsService} from "./services/reports/reports.service";
import {SelectionReportAudio} from "./reports/selection-report-audio";
import {SelectionRecordingStatus} from "./services/recording/selection-recording-status";
import {SelectionReportSummaryStatus} from "./reports/selection-report-summary-status";
import {SessionService} from "./services/configuration/session.service";
import {SessionStoragePersistenceStore} from "./services/persistence/session.storage.persistence.store";
import {StartupHttpService} from "./services/http/startup.http.service";
import {StudentConfiguration} from "./services/assignment/student.configuration";
import {TimerManagerService} from "./services/configuration/timer.manager.service";
import {UtilitiesService} from "./services/utilities.service";
import {UploadService} from "./services/interaction/upload.service";
import {KeyboardService} from "./services/input/keyboard-service";
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from "@angular/common/http";
import {BrowserBoot} from "./startup/browser.boot";
import {AppConfigLoader} from "./startup/app-config-loader";
import {Authenticator} from "./services/slc/slc.authenticator";
import {CustomHeaderInterceptor} from "./interceptor/custom-header-interceptor";
import {HideRAComponent} from "./hide.ra.component";
import {TranslateHttpLoader} from "@ngx-translate/http-loader";
import {TranslateLoader, TranslateModule} from "@ngx-translate/core";
import {PipelineModalComponent} from "./pipeline/pipeline.modal.component";
import {PipelineScriptModalComponent} from "./pipeline/pipeline.batch.modal.component";
import {PipelinePronunciationsModalComponent} from "./pipeline/pipeline.pronunciations.modal.component";
import {QuestionReportModalComponent} from "./pipeline/question.report.modal.component";
import {FormsModule} from "@angular/forms";
import {SummaryComponent} from "./summary/summary.component";
import {BugsnagService} from "./services/logging/bugsnag.service";
import {UserModelState} from "./services/usermodel/usermodel.state";
import Bugsnag from "@bugsnag/js";
import {PipelineModule} from "./pipeline/pipeline.module";
import { FeatureFlagPipeModule } from "./feature-flag/feature-flag.module";

export function TranslationLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, "i18n/", ".json");
}
Bugsnag.start({
    apiKey: BugsnagService.NOTIFIER_API_KEY,
    // TODO: not sure the difference betw appVersion and app.version? in FFWD client they are
    // pulled from different places
    appVersion: UtilitiesService.getVersion(),
    releaseStage: UtilitiesService.getReleaseStage(),
});

// create a factory which will return the Bugsnag error handler
export function errorHandlerFactory() {
    return new BugsnagErrorHandler();
}

@NgModule({
    declarations: [
        AppComponent,
        HelpComponent,
        CreditsComponent,
        Counto,
        DebugLogModalComponent,
        FluencyModalComponent,
        MessageModalComponent,
        MicrophoneCheckModalComponent,
        NavComponent,
        PleaseWaitComponent,
        SelectionModalComponent,
        SysinfoComponent,
        HideRAComponent,
        PipelineModalComponent,
        PipelineScriptModalComponent,
        PipelinePronunciationsModalComponent,
        QuestionReportModalComponent,
        SummaryComponent,
    ],
    imports: [
        FormsModule,
        BrowserModule,
        CommonModule,
        AppRoutingModule,
        ReadingModule,
        LibraryModule,
        HttpClientModule,
        TranslateModule.forRoot({
            loader: {provide: TranslateLoader, useFactory: TranslationLoaderFactory, deps: [HttpClient]}
        }),
        PipelineModule,
    ],
    providers: [
        {
            provide: ErrorHandler,
            useFactory: errorHandlerFactory },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CustomHeaderInterceptor,
            multi: true
        },
        {
            provide: LocationStrategy,
            useClass: HashLocationStrategy
        },
        Login,
        AppConfigLoader,
        Authenticator,
        ClientConfiguration,
        Location,
        AssignmentService,
        AudioFormatService,
        AudioKeyParser,
        AudioPlaybackService,
        AudioQueueService,
        BrowserBoot,
        BrowserAgent,
        ContentServerService,
        DebugService,
        EventBus,
        FluencyReportAudio,
        KeyboardService,
        LocalStoragePersistenceStore,
        BugsnagLoggingService,
        BugsnagService,
        MessageService,
        MicrophoneCheckDataService,
        MicrophoneService,
        ModalConfig,
        NavService,
        NetworkService,
        PersistenceService,
        PipelineService,
        PointService,
        RecognizerService,
        ReportsService,
        SelectionReportAudio,
        SelectionRecordingStatus,
        SelectionReportSummaryStatus,
        SessionService,
        SessionStoragePersistenceStore,
        StartupHttpService,
        StudentConfiguration,
        TimerManagerService,
        UploadService,
        UserModelState,
        UtilitiesService,
        {
            provide: String,
            useValue: ""
        }
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
